export const IMAGE_COMPRESSION_THRESHOLD = 500 * 1024;
export const IMAGE_UPLOAD_WARNING_THRESHOLD = 1024 * 1024;
const IMAGE_COMPRESSION_TARGET = 300 * 1024;
const IMAGE_MAX_DIMENSION = 1280;
const IMAGE_MIN_DIMENSION = 640;
const IMAGE_INITIAL_QUALITY = 0.7;
const IMAGE_MIN_QUALITY = 0.4;

export const formatImageSize = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const fetchImageSize = async (url: string, signal: AbortSignal): Promise<number> => {
  // 优先只读取响应头，避免为了显示大小重复下载整张图片。
  try {
    const response = await fetch(url, { method: 'HEAD', signal });
    const contentLength = response.headers.get('content-length');
    const size = contentLength === null ? NaN : Number(contentLength);
    if (response.ok && Number.isFinite(size) && size > 0) return size;
  } catch (error) {
    if (signal.aborted) throw error;
    console.warn('读取图片大小响应头失败，尝试读取图片内容:', error);
  }

  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('无法读取图片大小');
  return (await response.blob()).size;
};

// 目标 300 KB：PNG 保留透明背景，其他格式转换为 JPEG，逐步降低质量和尺寸。
export const compressImage = async (file: File): Promise<File> => {
  if (file.size <= IMAGE_COMPRESSION_THRESHOLD) return file;

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const source = new Image();
      source.onload = () => resolve(source);
      source.onerror = () => reject(new Error('图片无法读取，请选择其他图片'));
      source.src = url;
    });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前浏览器不支持图片压缩');

    const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const scale = Math.min(
      1,
      IMAGE_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight),
    );
    let width = Math.max(1, Math.round(image.naturalWidth * scale));
    let height = Math.max(1, Math.round(image.naturalHeight * scale));
    let quality = IMAGE_INITIAL_QUALITY;
    let smallestFile = file;

    for (let attempt = 0; attempt < 16; attempt++) {
      canvas.width = width;
      canvas.height = height;
      if (mimeType === 'image/jpeg') {
        context.fillStyle = '#fff';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(image, 0, 0, width, height);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) resolve(result);
            else reject(new Error('图片压缩失败，请选择其他图片'));
          },
          mimeType,
          quality,
        );
      });
      if (blob.size < smallestFile.size) {
        const extension = blob.type === 'image/png' ? 'png' : 'jpg';
        const name = `${file.name.replace(/\.[^.]+$/, '')}.${extension}`;
        smallestFile = new File([blob], name, {
          type: blob.type,
          lastModified: file.lastModified,
        });
      }
      if (smallestFile.size <= IMAGE_COMPRESSION_TARGET) return smallestFile;
      if (mimeType === 'image/jpeg' && quality > IMAGE_MIN_QUALITY) {
        quality = Math.max(IMAGE_MIN_QUALITY, quality - 0.15);
      } else {
        // 长边最低保留 640px，避免为了体积把菜品图缩得过小。
        const longestSide = Math.max(width, height);
        if (longestSide <= IMAGE_MIN_DIMENSION) return smallestFile;
        const resizeScale = Math.max(0.7, IMAGE_MIN_DIMENSION / longestSide);
        width = Math.max(1, Math.round(width * resizeScale));
        height = Math.max(1, Math.round(height * resizeScale));
      }
    }
    return smallestFile;
  } finally {
    URL.revokeObjectURL(url);
  }
};
